import { MousePointerClick, Package, Paintbrush, UploadCloud, Banknote, FileText, AudioLines, Boxes, Compass, Timer, Wallet, CalendarClock, CalendarOff } from "lucide-react";

const steps = [
  { icon: MousePointerClick, title: "Choose a job", text: "Browse available jobs on your worker dashboard and claim what fits your availability." },
  { icon: Package, title: "Get everything", text: "Script, pre-edited audio, avatars, references, and instructions — all in the job card." },
  { icon: Paintbrush, title: "Create it", text: "Animate, write, or edit with creative freedom. Follow directed references where given." },
  { icon: UploadCloud, title: "Submit it", text: "Send the finished work through the production workflow before the deadline." },
  { icon: Banknote, title: "Get paid", text: "Once the job is reviewed and accepted, payment is sent — simple as that." },
];

const includes = [
  { icon: FileText, label: "Full script & storyline" },
  { icon: AudioLines, label: "Pre-edited audio (voice + music + SFX)" },
  { icon: Boxes, label: "Avatars & assets" },
  { icon: Compass, label: "References & directions" },
  { icon: Timer, label: "Target duration" },
  { icon: Wallet, label: "Estimated pay" },
  { icon: CalendarClock, label: "Clear deadline" },
];

export const WorkflowSection = () => {
  return (
    <section id="workflow" className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center">
          <p className="font-mono-label text-xs font-bold uppercase tracking-widest text-cyan-600">Production workflow</p>
          <h2 data-testid="workflow-heading" className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl md:text-4xl">
            How production works
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            Once you're accepted, available jobs live on <span className="font-semibold text-slate-900">your worker dashboard</span>.
            Every job comes with everything you need — you just choose it, create it, and submit it.
          </p>
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {steps.map((step, i) => (
            <div
              key={step.title}
              data-testid={`workflow-step-${i + 1}`}
              className="relative rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <span className="absolute right-5 top-5 font-display text-4xl font-extrabold text-slate-100">{i + 1}</span>
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600">
                <step.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-base font-bold text-slate-900">{step.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">{step.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="rounded-3xl border border-slate-200/80 bg-slate-50/60 p-8 sm:p-10">
            <h3 className="font-display text-lg font-bold text-slate-900">Every job card includes</h3>
            <p className="mt-2 text-sm text-slate-500">
              For animation jobs, the audio is already finished — voiceover, music, and basic effects — so you animate directly around the final audio.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {includes.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700"
                >
                  <Icon className="h-3.5 w-3.5 text-cyan-600" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-col justify-center rounded-3xl bg-cyan-500 p-8 text-white sm:p-10">
            <CalendarOff className="h-8 w-8" />
            <h3 data-testid="workflow-flexibility-title" className="mt-4 font-display text-xl font-bold">
              No fixed schedule. Ever.
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-cyan-50">
              The system runs on individual jobs, not daily shifts. Take jobs when you're available — consistent
              work keeps coming as the channel scales. The pay for each job is shown upfront, so you always know
              what a job is worth before you take it.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
